/** importing libraries **/
import axios, { AxiosRequestConfig } from "axios";
import { toast } from "../utils/toast";
import { extractErrorMessage } from "../utils/errorUtils";
import { buildLoginPath } from "../utils/returnPath";

export interface AxiosRequestOptions {
    /**
     * When true, the response interceptor will NOT show an error toast for
     * this request. Use for flows that render the API error inline (e.g.,
     * field-level form validation).
     */
    silentError?: boolean;
    /**
     * Per-request axios timeout in ms. Omit for axios default (no timeout).
     * Use a generous value for public test flows that can run slow under load.
     */
    timeout?: number;
}

type AxiosConfigWithOptions = AxiosRequestConfig & AxiosRequestOptions;

// Routes that never require an authenticated session. A 401 while on one of
// these must NOT trigger a logout/redirect (avoids loops and spurious logouts).
const PUBLIC_PATH_PREFIXES = [
    "/login",
    "/owner-login",
    "/register",
    "/owner-register",
    "/forget-password",
    "/reset-password",
    "/test/",
    "/a/",
    "/assess/",
    "/evaluation/",
    "/one-time-audits",
    // A report opened from a share link: no account, so a stale token in
    // this browser must not log the visitor out and bounce them to /login.
    "/shared/",
    // Workshop certificate links: teachers have no account.
    "/certificate/",
    // Teen Insight 360 family report links: parents have no account.
    "/family-report/",
    // Direct-to-consumer links: a bought test and its report. Buyers have no account.
    "/take/",
    "/my-report/",
    "/my-invoice/",
];

// Substrings that identify an auth/credential request itself. A 401 from these
// is a normal "wrong credentials"/"not logged in yet" response that the calling
// page surfaces inline - it must never force a redirect.
const AUTH_REQUEST_HINTS = [
    "/login",
    "/register",
    "/forget-password",
    "/reset-password",
    "/owner-login",
    "/owner-register",
];

const isWaveYearSchoolPath = (pathname: string): boolean =>
    /^\/(BL|EL)\/\d{4}\/[^/]+$/i.test(pathname);

const isPublicPath = (pathname: string): boolean =>
    isWaveYearSchoolPath(pathname) ||
    PUBLIC_PATH_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(prefix)
    );

// Guard so concurrent 401s (e.g. several requests in flight when a token
// expires) only ever produce a single logout + redirect.
let isHandlingAuthExpiry = false;

/**
 * On a genuine session-expiry 401 - a request that carried a token but the
 * server rejected it - log the user out and send them to the login screen.
 *
 * Deliberately conservative so it never logs anyone out "for no reason":
 *  - only acts on HTTP 401 (not 403/permission, not 5xx, not network errors);
 *  - only when an access token actually exists (so it's a stale session, not a
 *    pre-login request);
 *  - never on auth endpoints themselves (wrong-password 401s stay inline);
 *  - never on public / anonymous-invitation routes (prevents redirect loops).
 */
const handleAuthExpiry = (error: any): void => {
    if (error?.response?.status !== 401) return;

    // Skip the auth/credential requests themselves (a wrong-password 401 must
    // stay inline, never redirect).
    const reqUrl = String(error?.config?.url ?? "").toLowerCase();
    if (AUTH_REQUEST_HINTS.some((hint) => reqUrl.includes(hint))) return;

    const { pathname, search } = window.location;
    if (isPublicPath(pathname)) return;

    // Anonymous one-time-audit / invitation flows carry their own token and must
    // not be bounced to the login screen.
    const params = new URLSearchParams(search);
    const hasInvitationToken = Boolean(
        params.get("token") ||
            params.get("invitationToken") ||
            localStorage.getItem("invitation_token") ||
            localStorage.getItem("anonymous_invitation_token")
    );
    if (hasInvitationToken) return;

    if (isHandlingAuthExpiry) return;
    isHandlingAuthExpiry = true;

    void (async () => {
        let owner = false;
        let shouldRedirect = false;
        try {
            // Dynamic import avoids a static circular dependency
            // (store -> slices -> api services -> ApiCall).
            const { store } = await import("../redux/store");
            const { logout } = await import("../redux/auth/auth.slice");
            const state = store.getState();
            const hadToken = Boolean(localStorage.getItem("access_token"));
            const believesAuthenticated = Boolean(state?.auth?.isAuthenticated);

            // Only treat this as a session expiry if there actually WAS a
            // session - a stored token (expired/rejected) or the app currently
            // considering itself logged in (token went missing mid-session).
            // If neither holds, do nothing: there's no session to end, so we
            // never log anyone out "for no reason".
            if (hadToken || believesAuthenticated) {
                shouldRedirect = true;
                owner = Boolean(state.auth.ownerMode);
                // Clears the token + auth state. App-level routing reacts to the
                // isAuthenticated change and navigates to /login (keeps the toast
                // visible, no hard reload).
                store.dispatch(logout());
            }
        } catch {
            // Store unreachable: fall back to a plain token check + clear.
            if (localStorage.getItem("access_token")) {
                shouldRedirect = true;
                try {
                    localStorage.removeItem("access_token");
                } catch {
                    /* ignore */
                }
            }
        }

        if (!shouldRedirect) {
            isHandlingAuthExpiry = false;
            return;
        }

        // Guaranteed redirect fallback: if app-level routing hasn't already
        // moved us off the protected page shortly after, hard-redirect.
        setTimeout(() => {
            if (!isPublicPath(window.location.pathname)) {
                window.location.replace(
                    buildLoginPath(window.location, { owner })
                );
            }
            isHandlingAuthExpiry = false;
        }, 1200);
    })();
};

// Attach a single global response interceptor so every axios call in the app
// produces exactly one error toast surfacing the backend's actual message.
// Callers can opt out per-request via { silentError: true }.
let interceptorInstalled = false;
const installGlobalErrorInterceptor = () => {
    if (interceptorInstalled) return;
    interceptorInstalled = true;
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const cfg = (error?.config ?? {}) as AxiosConfigWithOptions;
            const skip =
                cfg.silentError === true ||
                error?.code === "ERR_CANCELED" ||
                (typeof axios.isCancel === "function" && axios.isCancel(error));

            if (!skip) {
                toast.error(extractErrorMessage(error));
            }

            // Self-guarded: only redirects on a genuine session-expiry 401.
            handleAuthExpiry(error);

            return Promise.reject(error);
        }
    );
};

installGlobalErrorInterceptor();

export const AxiosRequest = async (
    methods?: any,
    url?: any,
    body?: any,
    header?: any,
    options: AxiosRequestOptions = {}
) => {
    const config: AxiosConfigWithOptions = {
        method: methods,
        url,
        headers: header
            ? header
            : {
                  "Content-Type": "application/json",
              },
        data: body,
        silentError: options.silentError,
        ...(typeof options.timeout === "number" ? { timeout: options.timeout } : {}),
    };

    return axios(config);
};
